
import { defineComponent, h, PropType, renderSlot, ref, useSlots, useAttrs, onMounted, defineEmits, computed, watch, watchEffect, toRefs, nextTick } from 'vue'

export default defineComponent({
  props: {
    data: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    columns: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    headH: {
      type: Number,
      default: 38
    },
    itemH: {
      type: Number,
      default: 32
    },
    bottomOffset: {
      type: Number,
      default: 3
    },
    openVirtual: {
      type: Boolean,
      default: false
    }
  },
  emits: ['onReachTop', 'onReachBottom'],
  setup (props, ctx) {
    const slots = useSlots();
    const attrs = useAttrs();

    const columns = slots.default ? slots.default() : [] as any
    const { data, headH, itemH, openVirtual } = toRefs(props)
    // 展示起点
    const start = ref<number>(0)
    // 展示数量
    const showNum = ref(10)
    // 滚动条距离顶部距离
    const scrollH = ref<number>(0)
    // 外部盒子高度
    const wrapH = ref(0)
    // 展示内容高度
    const tabH = ref(0)
    // 内容可视区域
    const bodyWrapRef = ref()
    // 内容全高区域
    const bodyInnerRef = ref()
    // 表头可视区域
    const headerWrapRef = ref()
    // 表头
    const theadRef = ref()
    // 内容
    const tbodyRef = ref()
    // 数据偏移量
    const offset = 5

    const showList = computed(() => {
      if (openVirtual.value) {
        const s = start.value - offset < 0 ? 0 : start.value - offset
        const num = showNum.value + 2 * offset
        return data.value.slice(s, s + num)
      }
      return data.value
    })

    const sortIndex = computed(() => {
      return (index) => {
        if (openVirtual.value) {
          return index + (start.value - offset < 0 ? 0 : start.value - offset)
        }
        return index
      }
    })

    onMounted(() => {
      init()
    })

    watch(
      () => data.value,
      () => {
        init()
      }
    )

    watch(
      () => wrapH.value,
      (val: number) => {
        if (!val) {
          return
        } 
        showNum.value = Math.ceil(val / itemH.value)
      }
    )

    watch(
      () => scrollH.value,
      (val: number) => {
        // 到顶部了
        if (val <= 0) {
          console.log('--到顶了---')
          ctx.emit('onReachTop')
        }
        // 到底部了
        if (wrapH.value + val >= tabH.value) {
          console.log('--到底了---')
          ctx.emit('onReachBottom')
        }
      }
    )

    // 解决数据量变化时，滚动条的位置
    watch(
      () => showList.value,
      (val) => {
        if (val.length === 0) {
          bodyWrapRef.value.scrollTop = 0
          start.value = 0
          tbodyRef.value.style.transform = `translateY(${start.value * itemH.value}px)`
        }
      }
    )

    watch(
      () => openVirtual.value,
      (val: boolean) => {
        if (!val) {
          tbodyRef.value.style.transform = `translateY(0px)`
        } else {
          scrollListener()
        }
      }
    )

    function init () {
      nextTick(() => {
        const innerHeight = bodyInnerRef.value.offsetHeight // 列表所有数据的高度
        const visibleHeight = bodyWrapRef.value.offsetHeight // 列表可视区域的高度
        scrollH.value = bodyWrapRef.value.scrollTop
        wrapH.value = visibleHeight
        headerWrapRef.value.style.overflow = innerHeight > visibleHeight ? 'hidden scroll' : 'hidden hidden' // 表头包裹区域是否要展示滚动条
        showNum.value = Math.floor(wrapH.value / itemH.value)
        tabH.value = openVirtual.value ? data.value.length * itemH.value : (tbodyRef.value.offsetHeight || 0)
      })
    }

    function scrollListener () {
      scrollH.value = bodyWrapRef.value.scrollTop
      if (openVirtual.value) {
        start.value = Math.ceil(scrollH.value / itemH.value)
        const s = start.value - offset < 0 ? 0 : start.value - offset
        tbodyRef.value.style.transform = `translateY(${s * itemH.value}px)`
      }
      theadRef.value.style.transform = `translateX(-${bodyWrapRef.value.scrollLeft}px)`
    }

    return () => (
      h('div', { 
        ...attrs
       }, [
        h('div', { 
          ref: headerWrapRef,
          class: 'transparent-scrollbar'
        },
          h('table', {
            ref: theadRef,
            style: { 'table-layout': 'fixed', width: '100%' }
          }, [
            h('colgroup', columns.map(column => h('col', { width: column?.props?.width || 'auto' }))),
            h('thead', [
              h('tr', { height: headH.value }, columns.map(column => {
                if (column.children) {
                  if (column.children.header) {
                    return h('th', { class: ['ew_table_head_th'] }, h('div', { class: 'cell' }, renderSlot(column.children, 'header')))
                  }
                }
                return h('th', { class: ['ew_table_head_th'] }, h('div', { class: 'cell', title: column?.props?.label }, column?.props?.label || ''))
              }))
            ])
          ])
        ),
        h('div',
          {
            ref: bodyWrapRef, 
            onScroll: scrollListener,
            style: {
              height: `calc(100% - ${headH.value}px)`,
              overflow: 'auto',
              position: 'relative'
            },
            class: ['scrollbar']
          },
          h('div', 
            {
              ref: bodyInnerRef,
              style: {
                height: openVirtual.value ? data.value.length * itemH.value + 'px' : 'auto'
              }
            },
            h('table', 
              {
                ref: tbodyRef,
                style: { 'table-layout': 'fixed', width: '100%' } 
              }, 
              [
                h('colgroup', columns.map(column => h('col', { width: column?.props?.width || 'auto' }))),
                h('tbody',
                  showList.value.length > 0 ? showList.value.map((item, index) => 
                    h('tr', { height: openVirtual.value ? itemH.value : 'auto' }, columns.map(column => { 
                      if (column.children) {
                        if (column.children.default) {
                          return h('td', { class: ['ew_table_body_td', openVirtual.value && 'lazy'] }, h('div', {
                            class: 'cell',
                            style: {
                              'min-width': (column?.props?.width + 'px') || 'auto'
                            }
                          }, renderSlot(column.children, 'default', { data: item, index: sortIndex.value(index) })))
                        }
                      }
                      return h('td', {
                        class: ['ew_table_body_td', openVirtual.value && 'lazy']
                      }, h('div', { 
                        class: 'cell',
                        title: item[column?.props?.prop],
                        style: {
                          'min-width': (column?.props?.width + 'px') || 'auto'
                        }
                      }, item[column?.props?.prop]))
                    }))
                  ) : h('tr', 
                        { 
                          height: openVirtual.value ? itemH.value : 'auto' 
                        }, 
                        [
                          h('td', { colspan: columns.length }, h('div', { style: { padding: '2px 4px', color: 'var(--font-color-06)' } }, '暂无数据'))
                        ]
                      )
                )
              ]
            )
          )
        )
      ])
    )
  }
})
